import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCurrentLang } from "../hooks/useCurrentLang";
import GlassCard from "../components/GlassCard";

const sections = [
  {
    title: "Who Revimo is and what this page covers",
    content: [
      "Welcome to Revimo. These Terms of Service explain the rules for using our website, app, workout plans, exercise suggestions, and related content.",
      "By using Revimo, you agree to these terms. This page is written in plain language so users can understand the most important points quickly.",
    ],
  },
  {
    title: "Revimo is not medical advice",
    content: [
      "Revimo provides general fitness, wellness, and exercise information only. Revimo does not provide medical advice, diagnosis, treatment, or healthcare services.",
      "Nothing on the platform should be treated as a substitute for advice from a qualified medical professional.",
      "Before starting any workout, training plan, or physical activity through Revimo, consult a doctor or another qualified healthcare professional, especially if you have any medical condition, injury, pain, dizziness, breathing problems, heart-related issues, or if you are pregnant or recovering from surgery or illness.",
    ],
  },
  {
    title: "Exercise risks and voluntary participation",
    content: [
      "Exercise and physical activity involve inherent risks. These risks can include discomfort, falls, strains, sprains, worsening of existing conditions, serious injury, illness, or other complications.",
      "You choose to participate voluntarily and at your own risk. You are responsible for deciding whether any workout, exercise, or plan is appropriate for your fitness level, health condition, and personal circumstances.",
      "If you feel pain, dizziness, weakness, shortness of breath, or any unusual symptoms, stop immediately and seek appropriate medical help.",
    ],
  },
  {
    title: "User responsibilities before and during exercise",
    content: [
      "You are responsible for using proper form and safe technique, exercising within your limits, making sure your environment is safe, checking that any equipment you use is suitable and in good condition, and seeking professional advice when needed.",
      "Revimo provides plans and guidance, but you are responsible for how you perform any exercise and whether you choose to perform it at all.",
    ],
    bullets: [
      "Use proper form and safe technique.",
      "Exercise within your own limits.",
      "Make sure your environment is safe.",
      "Check that any equipment you use is suitable and in good condition.",
      "Seek professional advice when needed.",
    ],
  },
  {
    title: "No guaranteed results",
    content: [
      "Revimo does not guarantee any specific fitness, health, weight-loss, performance, or physical results.",
      "Results vary from person to person based on many factors outside our control, including health status, consistency, sleep, nutrition, technique, effort, and individual physical differences.",
    ],
  },
  {
    title: "Equipment, environment, and third-party content",
    content: [
      "You are responsible for your workout space, your equipment, and your personal decision to follow any content on the platform.",
      "Revimo is not responsible for unsafe surroundings, faulty equipment, or third-party services, links, tools, or content that may be referenced or integrated into the platform.",
    ],
  },
  {
    title: "Limitation of liability",
    content: [
      "To the maximum extent permitted by law, Revimo is not liable for injuries, losses, damages, or complications arising from or related to your use of the platform, your participation in any exercise, your reliance on content provided through the platform, or your use of any equipment or third-party services.",
      'The platform is provided on an "as is" and "as available" basis, without warranties that it will always be error-free, uninterrupted, suitable for your needs, or produce any particular result.',
      "Nothing in these terms excludes liability that cannot legally be excluded under applicable law.",
    ],
  },
  {
    title: "Who can use the service and basic account rules",
    content: [
      "You agree to use Revimo lawfully and responsibly. You must not misuse the platform, interfere with its operation, copy or redistribute protected content without permission, or upload unlawful, harmful, or infringing material.",
      "If Revimo allows user-generated content, you remain responsible for the content you upload or share.",
    ],
  },
  {
    title: "Changes to these terms and contact details",
    content: [
      "We may update these Terms of Service from time to time. Continued use of Revimo after changes take effect means you accept the updated terms.",
      "For questions about these terms, contact: info@revimo.eu",
    ],
  },
];

const workoutNotice =
  "Revimo provides general fitness guidance, not medical advice. Exercise involves risk. By continuing, you confirm you participate voluntarily, at your own risk, and should stop immediately if you feel pain or discomfort.";

const styles = {

  main: {
    padding: "112px 16px 80px",
  },
  glassc: {
    padding: "20px",
  },
  glassc1: {
    padding: "20px",
    width: "100%",
    maxWidth: 1087,
    margin: "0 auto",
  },
  wrapper: {
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 32,
  },
  heroCard: {
    overflow: "hidden",
    maxWidth: "750px",
    border: "1px solid rgba(255,255,255,0.4)",
    background: "rgba(255,255,255,0.7)",
    padding: 0,
    boxShadow: "0 20px 60px rgba(11,23,32,0.08)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
  },
  heroInner: {
  },
  heroContent: {
    maxWidth: 760,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  updatedBadge: {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 999,
    border: "1px solid #b9d7de",
    background: "rgba(255,255,255,0.7)",
    padding: "6px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#4c6a7a",
  },
  heroTextGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  h1: {
    margin: 0,
    fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
    fontWeight: 600,
    letterSpacing: "-0.03em",
    color: "#0b1720",
    lineHeight: 1.05,
  },
  heroSubtitle: {
    margin: 0,
    maxWidth: 680,
    fontSize: "clamp(1rem, 2vw, 1.125rem)",
    lineHeight: 1.9,
    color: "#4c6a7a",
  },
  noticeCard: {
    border: "1px solid #d6e8ee",
    background: "rgba(255,255,255,0.75)",
    padding: 20,
    boxShadow: "none",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  },
  noticeLabel: {
    margin: "0 0 8px",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    color: "#4c6a7a",
  },
  noticeText: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.8,
    color: "#0b1720",
  },
  sectionGrid: {
    width: "100%",
    maxWidth: 896,
    margin: "0 auto",
    display: "grid",
    gap: 20,
  },
  sectionCard: {
    border: "1px solid rgba(255,255,255,0.5)",
    background: "rgba(255,255,255,0.72)",
    padding: 28,
    boxShadow: "0 12px 40px rgba(11,23,32,0.06)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
  },
  sectionInner: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  h2: {
    margin: 0,
    fontSize: "clamp(1.5rem, 3vw, 2rem)",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
    color: "#0b1720",
  },
  paragraphGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  paragraph: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.9,
    color: "#4c6a7a",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: "4px 0 0",
    display: "grid",
    gap: 12,
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    color: "#4c6a7a",
    fontSize: 16,
    lineHeight: 1.9,
  },
  bullet: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#7fc4cf",
    marginTop: 12,
    flexShrink: 0,
  },
  bottomWrap: {
    width: "100%",
    maxWidth: 896,
    margin: "0 auto",
  },
  bottomCard: {
    border: "1px solid #cfe3ea",
    background: "rgba(238,247,250,0.9)",
    padding: 32,
    boxShadow: "0 16px 40px rgba(11,23,32,0.06)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
  },
  bottomInner: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  warningBox: {
    borderRadius: 24,
    border: "1px solid rgba(255,255,255,0.6)",
    background: "rgba(255,255,255,0.8)",
    padding: 20,
    fontSize: 16,
    lineHeight: 1.9,
    color: "#0b1720",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  },
};

const getResponsiveStyles = () => {
  if (typeof window === "undefined") return styles;

  const isMobile = window.innerWidth < 640;

  return {
    ...styles,
    main: {
      ...styles.main,
      padding: isMobile ? "112px 16px 80px" : "112px 24px 80px",
    },
    heroInner: {
      ...styles.heroInner,
      padding: isMobile ? "40px 24px" : "48px 40px",
    },
    sectionCard: {
      ...styles.sectionCard,
      padding: isMobile ? 24 : 28,
    },
    bottomCard: {
      ...styles.bottomCard,
      padding: isMobile ? 24 : 32,
    },
  };
};

export const TermsOfService = ({ lang = "en" }) => {
  const currentLang = useCurrentLang();
  const s = getResponsiveStyles();

  return (
    <div style={s.page}>
      <Header lang={lang || currentLang} />

      <main style={s.main}>
        <div style={s.wrapper}>
          <GlassCard style={s.glassc1}>
            <div style={s.heroInner}>
              <div style={s.heroContent}>
                <div style={s.updatedBadge}>Last updated:   2. 4. 2026</div>

                <div style={s.heroTextGroup}>
                  <h1 style={s.h1}>Terms of Service</h1>
                  <p style={s.heroSubtitle}>
                  </p>
                </div>

                <GlassCard style={s.glassc}>
                  <div>
                    <p style={s.noticeLabel}>Important notice</p>
                    <p style={s.noticeText}>
                      Revimo is not medical advice. Exercise involves risk. Participate only if you believe you can do so safely.
                    </p>
                  </div>
                </GlassCard>
              </div>
            </div>
          </GlassCard>

          <div style={s.sectionGrid}>
            {sections.map((section) => (
              <GlassCard key={section.title} style={s.glassc}>
                <div style={s.sectionInner}>
                  <h2 style={s.h2}>{section.title}</h2>

                  <div style={s.paragraphGroup}>
                    {section.content.map((paragraph) => (
                      <p key={paragraph} style={s.paragraph}>
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {section.bullets && (
                    <ul style={s.list}>
                      {section.bullets.map((bullet) => (
                        <li key={bullet} style={s.listItem}>
                          <span style={s.bullet} />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </GlassCard>
            ))}
          </div>

          {/*<div style={s.bottomWrap}>
            <GlassCard>
              <div style={s.bottomInner}>
                <div>
                  <p style={s.noticeLabel}>Short warning for signup and workouts</p>
                  <h2 style={{ ...s.h2, marginTop: 8 }}>
                    Use this notice before users start exercising
                  </h2>
                </div>
                <p style={s.paragraph}>
                  This shorter message should appear near signup, onboarding, and before the first workout so users see it clearly.
                </p>
                <div style={s.warningBox}>{workoutNotice}</div>
              </div>
            </GlassCard>
          </div>*/}
        </div>
      </main>

      <Footer lang={lang || currentLang} />
    </div>
  );
};

export default TermsOfService;